import { Component } from 'vue-property-decorator';
import DateRangePicker from 'vue2-daterange-picker';
import { DateTime } from 'luxon';
import DatepickerRange from '@/components/ui/inputs/datepicker-range/datepickerRange.ts';
import isFunction from 'lodash/isFunction';
import { APP_KEY_DATEPICKER_PRESETS, getSavedState, saveState } from '@/helpers/local-storage.helper';
import { DateObjectUnits } from 'luxon/src/datetime';
import { DATEPICKER_FORMAT } from '@/helpers/date.helper';

interface ICustomRange {
  type: string,
  label: string,
}

@Component({
  components: { DateRangePicker }
})
export default class CustomDatepickerRange extends DatepickerRange {
  presets: ICustomRange[] = [
    {
      type: 'today',
      label: this.$t('datePicker.today') as string,
    },
    {
      type: 'last30minutes',
      label: this.$t('datePicker.last30minutes') as string,
    },
    {
      type: 'last1hour',
      label: this.$t('datePicker.last1hour') as string,
    },
    {
      type: 'last2hours',
      label: this.$t('datePicker.last2hours') as string,
    },
    {
      type: 'yesterday',
      label: this.$t('datePicker.yesterday') as string,
    },
    {
      type: 'last7Days',
      label: this.$t('datePicker.last7Days') as string,
    },
    {
      type: 'last30Days',
      label: this.$t('datePicker.last30Days') as string,
    },
    {
      type: 'thisMonth',
      label: this.$t('datePicker.thisMonth') as string,
    },
    {
      type: 'lastMonth',
      label: this.$t('datePicker.lastMonth') as string,
    },
    {
      type: 'anyDates',
      label: this.$t('datePicker.anyDates') as string,
    },
  ];

  mounted() {
    const savedPresets = getSavedState(APP_KEY_DATEPICKER_PRESETS);
    if (savedPresets?.[this.savedName]) {
      this.onClickRange(savedPresets[this.savedName])
    }
  }

  getPresetDates(type: string) {
    const units = { hour: 0, minute: 0, second: 0, millisecond: 0 };
    const unitsEnd = { hour: 23, minute: 59, second: 59, millisecond: 59 };
    const base = DateTime.fromJSDate(new Date(), { zone: 'utc' });

    switch (type) {
      case 'today':
        return [
          this.addZoneToDate(base, units),
          this.addZoneToDate(base, unitsEnd)
        ]
      case 'last30minutes':
        return [
          this.formatPresetDate(base.minus({ minutes: 30 }).set({ second: 0, millisecond: 0 })),
          this.formatPresetDate(base.set({ second: 0 }))
        ]
      case 'last1hour':
        return [
          this.formatPresetDate(base.minus({ hour: 1 }).set({ second: 0, millisecond: 0 })),
          this.formatPresetDate(base.set({ second: 0 }))
        ]
      case 'last2hours':
        return [
          this.formatPresetDate(base.minus({ hours: 2 }).set({ second: 0, millisecond: 0 })),
          this.formatPresetDate(base.set({ second: 0 }))
        ]
      case 'yesterday':
        return [
          this.addZoneToDate(base.minus({ day: 1 }), units),
          this.addZoneToDate(base.minus({ day: 1 }), unitsEnd)
        ]
      case 'last7Days':
        return [
          this.addZoneToDate(base.minus({ days: 6 }), units),
          this.addZoneToDate(base.set(unitsEnd), unitsEnd)
        ]
      case 'last30Days':
        return [
          this.addZoneToDate(base.minus({ days: 29 }), units),
          this.addZoneToDate(base.set(unitsEnd), unitsEnd)
        ]
      case 'thisMonth':
        return [
          this.addZoneToDate(base.startOf('month'), units),
          this.addZoneToDate(base.endOf('month').set(unitsEnd), unitsEnd)
        ]
      case 'lastMonth':
        return [
          this.addZoneToDate(base.minus({ month: 1 }).startOf('month'), units),
          this.addZoneToDate(base.minus({ month: 1 }).endOf('month').set(unitsEnd), unitsEnd)
        ]
      default: return [null, null]
    }
  }

  formatPresetDate(date: DateTime, units: DateObjectUnits = {}) {
    return date.set(units).toFormat(DATEPICKER_FORMAT)
  }

  onClickRange(type: string) {
    const dates = this.getPresetDates(type);
    this.savePreset(type);
    this.$emit('update:startDate', dates[0]);
    this.$emit('update:endDate', dates[1]);

    if (this.$listeners.update && isFunction(this.$listeners.update)) {
      this.$listeners.update(dates[0], dates[1]);
    }
  }

  isRangeActive(type: string) {
    const dates = this.getPresetDates(type);
    return this.startDate === dates[0] && this.endDate === dates[1];
  }

  dateCell(value) {
    return new Date(value).getDate()
  }

  get savedName() {
    return `${this.$route.name}-${this.$attrs.name || ''}`;
  }

  savePreset(type: string) {
    const savedPresets = getSavedState(APP_KEY_DATEPICKER_PRESETS) || {};
    savedPresets[this.savedName] = type;
    saveState(APP_KEY_DATEPICKER_PRESETS, savedPresets);
  }

  removePreset() {
    const savedPresets = getSavedState(APP_KEY_DATEPICKER_PRESETS) || {};
    if (savedPresets[this.savedName]) {
      delete savedPresets[this.savedName];
      saveState(APP_KEY_DATEPICKER_PRESETS, savedPresets);
    }
  }
}
